import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AutoReopenStatus,
  Operation,
  OperationFailure,
  OpeningRetryPrefill,
  Position,
  PositionsResponse,
} from "@auto-pools/core";
import { api, ApiError } from "./client";
import { useResource } from "./useResource";
import { priceFrame } from "../lib/priceFrame";

export interface PositionAction {
  /** Absent when the action is rebuilt from persisted operations without a visited position card. */
  position?: Position;
  /** Display pair when no position snapshot exists; never a substitute for position values. */
  pair?: string;
  cleanup: boolean;
  idempotencyKey: string;
  pending: boolean;
  submitted?: boolean;
  error: string | null;
  failure?: OperationFailure | null;
  operation?: Operation;
  acceptedEpoch?: number;
  notified?: boolean;
  hidden?: boolean;
  restart?: OpeningRetryPrefill;
  /** A server-run reopen sequence ended permanently; its cleanup is not a cancellation success. */
  autoReopenStopped?: boolean;
}
/** Latest observed member operation of one auto-reopen sequence and the status it carried. */
export interface SequenceObservation {
  sequence: AutoReopenStatus;
  operation: Operation;
}
function laterMember(candidate: SequenceObservation, current: SequenceObservation) {
  const created =
    Date.parse(candidate.operation.createdAt) - Date.parse(current.operation.createdAt);
  if (Number.isFinite(created) && created !== 0) return created > 0;
  return Date.parse(candidate.sequence.updatedAt) >= Date.parse(current.sequence.updatedAt);
}
export function usePositionActions(
  authenticated: boolean,
  visible: boolean,
  refresh: number,
  walletIdentity = "",
) {
  const [actions, setActions] = useState<Partial<Record<string, PositionAction>>>({});
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const [dialogId, setDialogId] = useState<string | null>(null);
  const [completionVersion, setCompletionVersion] = useState(0);
  const [snapshots, setSnapshots] = useState<Record<string, Position | undefined>>({});
  const [observed, setObserved] = useState<
    Record<string, { operation: Operation; acceptedEpoch: number; notified: boolean } | undefined>
  >({});
  const observedRef = useRef(observed);
  observedRef.current = observed;
  const observePositions = useCallback((positions: Position[]) => {
    setSnapshots((current) => ({
      ...current,
      ...Object.fromEntries(positions.map((position) => [position.id, position])),
    }));
  }, []);
  const epoch = useRef(0),
    lifetime = useRef(0),
    sessionEpoch = useRef(0);
  const wallet = useRef(walletIdentity);
  const live = useRef(authenticated);
  live.current = authenticated;
  const [sequences, setSequences] = useState<Record<string, SequenceObservation | undefined>>({});
  const sequenceSignature = useRef("[]");
  /** Last server phase seen per sequence; only a transition it reports may release a refresh. */
  const sequencePhases = useRef<Record<string, string | undefined>>({});
  const tracked = Object.values(actions)
    .filter((a): a is PositionAction => a !== undefined)
    .some((a) => a.operation && !["completed", "cancelled"].includes(a.operation.state));
  // A server-run sequence continues without a local operation, so its authority keeps the poll alive.
  const sequenceRunning = Object.values(sequences).some(
    (s) => s?.sequence.automationActive === true,
  );
  const polling =
    authenticated &&
    (visible ||
      tracked ||
      sequenceRunning ||
      Object.values(observed).some(
        (o) => o && !["completed", "cancelled"].includes(o.operation.state),
      ));
  const resource = useResource<{ items: Operation[]; epoch: number }>(
    `position-actions:${String(authenticated)}:${String(polling)}:${String(refresh)}`,
    async (signal) => {
      if (!polling) return { items: [], epoch: ++epoch.current };
      const readEpoch = ++epoch.current;
      const response = await api<{ items: Operation[] }>("/operations", {
        signal,
        cache: "no-store",
      });
      const knownIds = Object.values(actionsRef.current)
        .filter((a): a is PositionAction => a !== undefined)
        .flatMap((a) => (a.operation ? [a.operation.id] : []));
      const missing = [...new Set([...knownIds, ...Object.keys(observedRef.current)])].filter(
        (id) => !response.items.some((o) => o.id === id),
      );
      const details = await Promise.all(
        missing.map((id) =>
          api<Operation>(`/operations/${encodeURIComponent(id)}`, { signal, cache: "no-store" }),
        ),
      );
      return { items: [...response.items, ...details], epoch: readEpoch };
    },
    polling ? 15000 : undefined,
  );
  const reloadOperations = resource.reload;
  const loadPositionPermissions = useCallback(
    async (signal: AbortSignal, options: { snapshot?: boolean } = {}) => {
      const generation = lifetime.current;
      const current = () => {
        if (signal.aborted || !live.current || generation !== lifetime.current)
          throw new Error("Position verification session changed");
      };
      current();
      const positions = await api<PositionsResponse>("/positions", {
        signal,
        cache: "no-store",
        // The server answers from the last shared read when it is still valid, else reads live.
        ...(options.snapshot ? { headers: { "x-positions-source": "snapshot" } } : {}),
      });
      current();
      if (
        !positions.items.some(
          (p) =>
            p.operation?.kind === "open" &&
            ["blocked", "failed"].includes(p.operation.state) &&
            p.operation.opening?.executionVersion === 2,
        )
      )
        return { ...positions, operations: [] as Operation[], epoch: 0 };
      // This read starts after permission observations; earlier polls cannot win timestamp ties.
      const readEpoch = ++epoch.current;
      const operations = await api<{ items: Operation[] }>("/operations", {
        signal,
        cache: "no-store",
      });
      current();
      return { ...positions, operations: operations.items, epoch: readEpoch };
    },
    [],
  );
  useEffect(() => {
    if (authenticated && wallet.current === walletIdentity) return;
    wallet.current = walletIdentity;
    lifetime.current++;
    epoch.current++;
    sessionEpoch.current = epoch.current;
    actionsRef.current = {};
    setActions({});
    observedRef.current = {};
    setObserved({});
    setSnapshots({});
    sequenceSignature.current = "[]";
    sequencePhases.current = {};
    setSequences({});
    setDialogId(null);
  }, [authenticated, walletIdentity]);
  useEffect(() => {
    if (!authenticated) return;
    let completed = 0,
      changed = false,
      observationChanged = false,
      needsDetail = false;
    const next = { ...actionsRef.current };
    const observations = { ...observedRef.current };
    for (const action of Object.values(next)) {
      if (!action?.operation || observations[action.operation.id]) continue;
      observations[action.operation.id] = {
        operation: action.operation,
        acceptedEpoch: action.acceptedEpoch ?? 0,
        notified: action.notified === true,
      };
      observationChanged = true;
    }
    const candidates = [
      ...Object.values(snapshots).flatMap((p) => (p?.operation ? [p.operation] : [])),
      ...(resource.data && resource.data.epoch > sessionEpoch.current ? resource.data.items : []),
    ];
    const members = new Map<string, Operation>();
    const sequenceLatest: Record<string, SequenceObservation | undefined> = {};
    for (const operation of candidates) {
      const sequence = operation.autoReopen;
      if (!sequence) continue;
      members.set(operation.id, operation);
      const current = sequenceLatest[sequence.sequenceId];
      const candidate = { sequence, operation };
      if (!current || laterMember(candidate, current))
        sequenceLatest[sequence.sequenceId] = candidate;
    }
    for (const operation of candidates) {
      if (operation.kind !== "close") continue;
      // A sequence member is driven by the server; it is neither a local action nor a close notice.
      if (operation.autoReopen) continue;
      const previous = observations[operation.id];
      if (!previous) {
        if (["completed", "cancelled"].includes(operation.state)) continue;
        if (
          candidates.some(
            (other) =>
              other.kind === "close" &&
              other.positionId === operation.positionId &&
              other.id !== operation.id &&
              Date.parse(other.createdAt) > Date.parse(operation.createdAt),
          )
        )
          continue;
        observations[operation.id] = { operation, acceptedEpoch: 0, notified: false };
        observationChanged = true;
        if (!resource.data?.items.some((o) => o.id === operation.id)) needsDetail = true;
        continue;
      }
      const updatedAt = Date.parse(operation.updatedAt);
      const previousUpdatedAt = Date.parse(previous.operation.updatedAt);
      // Millisecond timestamps can tie; only an authoritative read may advance to completion.
      const authoritativeCompletion =
        operation.state === "completed" && resource.data?.items.includes(operation);
      if (
        (resource.data?.epoch ?? 0) <= previous.acceptedEpoch ||
        updatedAt < previousUpdatedAt ||
        (updatedAt === previousUpdatedAt && !authoritativeCompletion) ||
        ["completed", "cancelled"].includes(previous.operation.state)
      )
        continue;
      const final = operation.state === "completed" && !previous.notified;
      observations[operation.id] = {
        ...previous,
        operation,
        notified: previous.notified || final,
      };
      if (final) completed++;
      observationChanged = true;
    }
    for (const observation of Object.values(observations)) {
      if (!observation) continue;
      const { operation, notified } = observation;
      const id = operation.positionId;
      if (!id) continue;
      const action = next[id];
      if (action?.operation && action.operation.id !== operation.id) continue;
      const position = action?.position ?? snapshots[id];
      if (!position) continue;
      if (action?.operation === operation && action.notified === notified) continue;
      const newlyCompleted =
        operation.state === "completed" && notified && action?.notified !== true;
      next[id] = {
        ...(action ?? {
          position,
          cleanup: operation.step === "cleanup",
          idempotencyKey: crypto.randomUUID(),
          pending: false,
          error: null,
        }),
        operation,
        notified,
        ...(newlyCompleted ? { hidden: false } : {}),
      };
      changed = true;
    }
    // A permanently stopped sequence without any successor leaves the operator one recovery path.
    // The evidence is persisted, so a reload without any visited position card must still offer it.
    for (const latest of Object.values(sequenceLatest)) {
      if (!latest) continue;
      const { sequence } = latest;
      if (sequence.phase !== "needs-action" || sequence.automationActive) continue;
      if (sequence.successorOperationId ?? sequence.manualSuccessorOperationId) continue;
      const cleanup = members.get(sequence.cleanupOperationId);
      const id = cleanup?.positionId;
      if (!cleanup || cleanup.state !== "completed" || !id) continue;
      // A dialog draft that was never sent is not a command; only a submitted, in-flight or
      // operation-bearing action may keep this recovery from being reconstructed.
      const existing = next[id];
      if (existing?.submitted || existing?.pending || existing?.operation) continue;
      const prefill = members.get(sequence.sourceOperationId)?.opening?.prefill;
      const position = snapshots[id];
      const pair =
        position?.pair ?? (prefill ? priceFrame(prefill.tokenA, prefill.tokenB).pair : undefined);
      next[id] = {
        ...(position ? { position } : {}),
        ...(pair ? { pair } : {}),
        cleanup: true,
        autoReopenStopped: true,
        idempotencyKey: crypto.randomUUID(),
        pending: false,
        error: null,
        operation: cleanup,
        notified: true,
        ...(prefill ? { restart: prefill } : {}),
      };
      changed = true;
    }
    // The portfolio changes when a sequence finishes, but its members are excluded from the close
    // notices above, so the refresh follows the server phase the poll reports for the sequence.
    for (const latest of Object.values(sequenceLatest)) {
      if (!latest) continue;
      const { sequenceId, phase } = latest.sequence;
      const previous = sequencePhases.current[sequenceId];
      sequencePhases.current[sequenceId] = phase;
      if (previous && previous !== "completed" && phase === "completed") completed++;
    }
    // Member state (state, attempts, signature, timestamps) must republish even within one phase.
    const signature = JSON.stringify(sequenceLatest);
    if (signature !== sequenceSignature.current) {
      sequenceSignature.current = signature;
      setSequences(sequenceLatest);
    }
    if (observationChanged) {
      observedRef.current = observations;
      setObserved(observations);
    }
    if (changed) {
      actionsRef.current = next;
      setActions(next);
    }
    if (completed) setCompletionVersion((n) => n + completed);
    if (needsDetail) reloadOperations();
  }, [authenticated, resource.data, reloadOperations, snapshots]);
  const begin = useCallback((position: Position, restart?: OpeningRetryPrefill) => {
    const current = actionsRef.current[position.id];
    if (!current || (!current.submitted && !current.pending && !current.operation)) {
      const next: PositionAction = {
        position,
        cleanup:
          restart !== undefined ||
          position.monitorState === "opening" ||
          position.canCancelOpening === true,
        idempotencyKey: crypto.randomUUID(),
        pending: false,
        error: null,
        ...(restart ? { restart } : {}),
      };
      actionsRef.current = { ...actionsRef.current, [position.id]: next };
      setActions(actionsRef.current);
    }
    setDialogId(position.id);
  }, []);
  const submit = useCallback(
    async (id: string) => {
      const action = actionsRef.current[id];
      if (!action || action.pending || action.operation || !live.current) return;
      const generation = lifetime.current;
      const isCurrent = () => generation === lifetime.current && live.current;
      const update = (patch: Partial<PositionAction>) => {
        if (!isCurrent()) return;
        actionsRef.current = {
          ...actionsRef.current,
          [id]: { ...action, ...actionsRef.current[id], ...patch },
        };
        setActions(actionsRef.current);
      };
      update({ pending: true, submitted: true, error: null, failure: null });
      try {
        const accepted = await api<Operation>(`/positions/${encodeURIComponent(id)}/close`, {
          method: "POST",
          sensitive: true,
          body: {
            idempotencyKey: action.idempotencyKey,
            ...(action.cleanup ? { cleanupOnly: true } : {}),
          },
        });
        if (!isCurrent()) return;
        const previous = observedRef.current[accepted.id];
        const operation =
          previous && Date.parse(previous.operation.updatedAt) >= Date.parse(accepted.updatedAt)
            ? previous.operation
            : accepted;
        const acceptedEpoch = ++epoch.current;
        const notified = previous?.notified === true || operation.state === "completed";
        observedRef.current = {
          ...observedRef.current,
          [operation.id]: { operation, acceptedEpoch, notified },
        };
        setObserved(observedRef.current);
        update({
          operation,
          acceptedEpoch,
          pending: false,
          notified,
        });
        if (operation.state === "completed" && !previous?.notified)
          setCompletionVersion((n) => n + 1);
        setDialogId((current) => (current === id ? null : current));
        reloadOperations();
      } catch (error) {
        update({
          pending: false,
          failure: error instanceof ApiError ? error.operationFailure : null,
          error: error instanceof Error ? error.message : "Command was not accepted",
        });
      }
    },
    [reloadOperations],
  );
  const dismiss = useCallback(() => {
    setDialogId(null);
  }, []);
  const hideToast = useCallback((id: string) => {
    const a = actionsRef.current[id];
    if (a) {
      actionsRef.current = { ...actionsRef.current, [id]: { ...a, hidden: true } };
      setActions(actionsRef.current);
    }
  }, []);
  const consumeRestart = useCallback((id: string) => {
    const a = actionsRef.current[id];
    if (a?.operation?.state !== "completed" || !a.restart) return undefined;
    const draft = a.restart;
    const next = { ...a, hidden: true };
    delete next.restart;
    actionsRef.current = { ...actionsRef.current, [id]: next };
    setActions(actionsRef.current);
    return draft;
  }, []);
  return {
    actions,
    sequences,
    dialog: dialogId ? actions[dialogId] : undefined,
    begin,
    submit,
    dismiss,
    hideToast,
    consumeRestart,
    completionVersion,
    observePositions,
    loadPositionPermissions,
    operations: resource,
  };
}
export type PositionActions = ReturnType<typeof usePositionActions>;
